import type { PerfilAudio } from '@/shared/models/configuracoes.model';

/**
 * Cadeia de DSP (Web Audio) compartilhada pelas capturas HDMI e Janela.
 *
 * Monta: source → HighPass → LowShelf(graves) → Peaking(presença) → HighShelf(brilho)
 *               → widener estéreo (Mid/Side) → Compressor → GainNode → destination
 *
 * O preset apenas ajusta os parâmetros dos filtros; a topologia é fixa para evitar
 * reconstruções de grafo ao trocar de perfil. O perfil "natural" deixa a cadeia
 * transparente (ganhos 0 dB, largura 1, compressor em ratio 1), preservando o som atual.
 *
 * Música/YouTube fica fora desta cadeia por barreira cross-origin do IFrame.
 */

export interface CadeiaAudioImersiva {
  /** Volume base 0–100 (combina com ganho no GainNode final). */
  setVolume(v: number): void;
  /** Ganho 0–200 (100 = unity, > 100 amplifica). */
  setGanho(g: number): void;
  /** Troca o preset de realce. Aplica parâmetros sem recriar o grafo. */
  setPerfil(p: PerfilAudio): void;
  /** Desconecta e libera os nós da cadeia (não fecha o AudioContext). */
  disconnect(): void;
}

interface PerfilParams {
  hpfHz:        number;
  bassHz:       number;
  bassGain:     number;  // dB
  presenceHz:   number;
  presenceGain: number;  // dB
  presenceQ:    number;
  airHz:        number;
  airGain:      number;  // dB
  width:        number;  // 1 = identidade; > 1 alarga; < 1 centraliza
  compThreshold: number;
  compKnee:      number;
  compRatio:     number;
  compAttack:    number;
  compRelease:   number;
}

const PRESETS: Record<PerfilAudio, PerfilParams> = {
  natural: {
    hpfHz: 20,
    bassHz: 110, bassGain: 0,
    presenceHz: 2500, presenceGain: 0, presenceQ: 1,
    airHz: 8000, airGain: 0,
    width: 1,
    compThreshold: 0, compKnee: 0, compRatio: 1, compAttack: 0.003, compRelease: 0.25,
  },
  imersivo: {
    hpfHz: 30,
    bassHz: 110, bassGain: 5,
    presenceHz: 2500, presenceGain: 0, presenceQ: 1,
    airHz: 9000, airGain: 3,
    width: 1.4,
    compThreshold: -18, compKnee: 6, compRatio: 3, compAttack: 0.003, compRelease: 0.25,
  },
  voz: {
    hpfHz: 120,
    bassHz: 110, bassGain: 0,
    presenceHz: 2500, presenceGain: 4, presenceQ: 1,
    airHz: 6000, airGain: 2,
    width: 0.8,
    compThreshold: -22, compKnee: 6, compRatio: 4, compAttack: 0.003, compRelease: 0.2,
  },
};

/**
 * Widener estéreo Mid/Side construído com nós nativos.
 *
 * Mid  = 0.5·(L+R)   Side = 0.5·(L−R)
 * L'   = Mid + Side·w   R' = Mid − Side·w
 *
 * Fonte mono é segura: o up-mix duplica o canal (L=R) ⇒ Side=0 ⇒ saída inalterada.
 */
function criarWidener(ctx: AudioContext, input: AudioNode, width: number) {
  const splitter = ctx.createChannelSplitter(2);
  const mid      = ctx.createGain();  // 0.5·(L+R)
  const sidePos  = ctx.createGain();  // +0.5·L
  const sideNeg  = ctx.createGain();  // −0.5·R
  const side     = ctx.createGain();  // soma → 0.5·(L−R)
  const sideToL  = ctx.createGain();  // +w
  const sideToR  = ctx.createGain();  // −w
  const merger   = ctx.createChannelMerger(2);

  mid.gain.value     = 0.5;
  sidePos.gain.value = 0.5;
  sideNeg.gain.value = -0.5;
  side.gain.value    = 1;
  sideToL.gain.value = width;
  sideToR.gain.value = -width;

  input.connect(splitter);

  // Mid (soma dos dois canais)
  splitter.connect(mid, 0);
  splitter.connect(mid, 1);

  // Side (diferença dos canais)
  splitter.connect(sidePos, 0);
  splitter.connect(sideNeg, 1);
  sidePos.connect(side);
  sideNeg.connect(side);

  // Decode
  side.connect(sideToL);
  side.connect(sideToR);
  mid.connect(merger, 0, 0);
  sideToL.connect(merger, 0, 0);
  mid.connect(merger, 0, 1);
  sideToR.connect(merger, 0, 1);

  const setWidth = (w: number) => {
    sideToL.gain.value = w;
    sideToR.gain.value = -w;
  };

  const disconnect = () => {
    [splitter, mid, sidePos, sideNeg, side, sideToL, sideToR, merger].forEach(n => {
      try { n.disconnect(); } catch { /* já desconectado */ }
    });
  };

  return { output: merger as AudioNode, setWidth, disconnect };
}

export function criarCadeiaAudioImersiva(
  ctx: AudioContext,
  source: AudioNode,
  opts: { volume: number; ganho: number; perfil: PerfilAudio },
): CadeiaAudioImersiva {
  let volume = opts.volume;
  let ganho  = opts.ganho;

  const calcGain = () => (volume / 100) * (ganho / 100);

  const hpf      = ctx.createBiquadFilter();
  const bass     = ctx.createBiquadFilter();
  const presence = ctx.createBiquadFilter();
  const air      = ctx.createBiquadFilter();
  const comp     = ctx.createDynamicsCompressor();
  const gain     = ctx.createGain();

  hpf.type      = 'highpass';
  bass.type     = 'lowshelf';
  presence.type = 'peaking';
  air.type      = 'highshelf';

  // source → hpf → bass → presence → air
  source.connect(hpf);
  hpf.connect(bass);
  bass.connect(presence);
  presence.connect(air);

  // air → widener → comp → gain → destination
  const widener = criarWidener(ctx, air, PRESETS[opts.perfil].width);
  widener.output.connect(comp);
  comp.connect(gain);
  gain.connect(ctx.destination);

  gain.gain.value = calcGain();

  const aplicarPerfil = (p: PerfilAudio) => {
    const cfg = PRESETS[p];
    hpf.frequency.value      = cfg.hpfHz;
    bass.frequency.value     = cfg.bassHz;
    bass.gain.value          = cfg.bassGain;
    presence.frequency.value = cfg.presenceHz;
    presence.gain.value      = cfg.presenceGain;
    presence.Q.value         = cfg.presenceQ;
    air.frequency.value      = cfg.airHz;
    air.gain.value           = cfg.airGain;
    widener.setWidth(cfg.width);
    comp.threshold.value     = cfg.compThreshold;
    comp.knee.value          = cfg.compKnee;
    comp.ratio.value         = cfg.compRatio;
    comp.attack.value        = cfg.compAttack;
    comp.release.value       = cfg.compRelease;
  };

  aplicarPerfil(opts.perfil);

  return {
    setVolume(v: number) {
      volume = v;
      gain.gain.value = calcGain();
    },
    setGanho(g: number) {
      ganho = g;
      gain.gain.value = calcGain();
    },
    setPerfil(p: PerfilAudio) {
      aplicarPerfil(p);
    },
    disconnect() {
      [source, hpf, bass, presence, air, comp, gain].forEach(n => {
        try { n.disconnect(); } catch { /* já desconectado */ }
      });
      widener.disconnect();
    },
  };
}
